// PRPBiotinData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[];
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const prpBiotin: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Biotin PRP Hair Loss Treatment in Dubai | Elite Body Home",
            "description": "Experience advanced Biotin PRP hair loss treatment in Dubai at Elite Body Home. Restore your confidence with this powerful, non-surgical solution for thinning hair.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/prp-biotin-hair-loss-treatment/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Biotin PRP Hair Loss Treatment in Dubai | Elite Body Home",
                "description": "Experience advanced Biotin PRP hair loss treatment in Dubai at Elite Body Home. Restore your confidence with this powerful, non-surgical solution for thinning hair.",
                "url": "https://elitebodyhome.com/services/prp-biotin-hair-loss-treatment/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T09:27:20+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Biotin PRP Hair Loss Treatment",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Biotin PRP Hair Loss Treatment in Dubai | Elite Body Home",
                "description": "Discover safe and effective Biotin PRP hair loss treatment in Dubai at Elite Body Home. Boost your hair growth with our expert non-invasive therapy.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg"
            },
        },
        "breadcrumbs": ["Home", "Services", "Biotin PRP Hair Loss Treatment"],
        "hero": {
            "title": "PRP Facial Treatment Dubai",
            "subtitle": "Unlock Your Skin's Natural Glow with PRP",
            "description": "Finding a hair system to treat your hair loss can be a helpful choice that restores confidence and self-image."
        },
        "howItWorks": {
            "title": "What is the PRP Biotin Hair Loss Treatment?",
            "description": "It combines the power of PRP (platelet-rich plasma) with biotin, an essential vitamin for hair health. Whether you're dealing with thinning hair, male or female pattern baldness, or hair shedding, this treatment can be a minimally invasive, natural way to revive hair growth."
        },
        "procedureSteps": {
            "heading": "Procedure",
            "para": "The Biotin PRP (Platelet Rich Plasma) Hair Loss Treatment is a combination of two highly effective methods; both the extraction of PRP from your own blood and the infusion of biotin, which creates a powerhouse mixture capable of feeding your scalp. With this technique hair follicles are rejuvenated, maintains blood circulation and increases density.",
            "steps": [
                {
                    "title": "Consultation",
                    "desc": "Biotin PRP starts with a consultation with a specialist who assesses your hair loss issues and ascertains whether this treatment is suitable for you.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "Blood Draw",
                    "desc": "Only a few milliliters of blood are drawn and placed into a PRP tube with biotin for regenerative effects.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "PRP Preparation",
                    "desc": "The blood is spun in a centrifuge so that platelet-rich plasma (PRP) becomes separated from the rest of the components, thus achieving a high concentration.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "Scalp Injection",
                    "desc": "PRP With Biotin is then injected into the areas of the scalp where hair thinning or loss is being experienced.",
                    "icon": "fa-solid fa-needle"
                },
                {
                    "title": "Biotin Infusion",
                    "desc": "PRP is fortified with biotin, a vitamin that helps your hair grow healthily.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "Recovery & Results",
                    "desc": "The PRP acts on hair follicles over time to promote natural hair and designed to thicken. For best results, multiple sessions may be recommended.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "benefits": {
            "heading": "So, what are the benefits of PRP Biotin for hair loss?",
            "subheading": "PRP + BIOTIN: A guide on How PRP + Biotin can help fight hair loss?",
            "para": "The combination of PRP and Biotin is a strong synergy to restore your hair thickness and shine. Here are the key benefits:",
            "items": [
                {
                    "title": "Stimulates Hair Growth",
                    "desc": "Platelet-rich plasma (PRP) contains growth factors that stimulate inactive hair follicles and promote the resumption of hair growth.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "Improves Hair Strength",
                    "desc": "Biotin helps hair grow stronger and thicker without the risk of breakage and thinning.",
                    "icon": "fa-solid fa-shield-halved"
                },
                {
                    "title": "Natural and Non-Invasive",
                    "desc": "Because PRP comes from your own blood, the treatment is safe and has little risk of an allergic reaction.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "Customizable Treatment",
                    "desc": "The treatment can be personalized to best suit individual needs depending on the degree of hair loss as well as the condition of the scalp.",
                    "icon": "fa-solid fa-sliders"
                },
                {
                    "title": "Minimal Downtime",
                    "desc": "However, recovery time is not necessary; patients can get back to their normal activities right away.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "Boosts Scalp Health",
                    "desc": "Biotin is known to promote scalp health and encourages healthier hair overall.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "eligibility": {
            "heading": "Who Can Have PRP Biotin Hair Fall Treatment?",
            "subheading": "",
            "para": "The Biotin PRP Hair Loss Treatment is suitable for many people experiencing hair loss. This treatment is aimed at anyone who has hair thinning, early hair loss, or even advanced baldness. Here's who could stand to gain the most:",
            "items": [
                {
                    "title": "Men and women with pattern baldness",
                    "desc": "(androgenetic alopecia)",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "People dealing with hair thinning",
                    "desc": "from old age or hormonal changes.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "Postpartum women",
                    "desc": "Women who have just given birth and losing hair post pregnancy",
                    "icon": "fa-solid fa-person-breastfeeding"
                },
                {
                    "title": "Family history of hair thinning",
                    "desc": "",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "Temporary hair loss",
                    "desc": "People losing hair due to illness or surgery that has caused temporary hair loss.",
                    "icon": "fa-solid fa-user-injured"
                },
                {
                    "title": "Alopecia patients",
                    "desc": "Patients with alopecia areata or other scalp issues (although a consult is needed)",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to prepare PRP Biotin Hair Loss Treatment?",
            "subheading": "",
            "para": "Your clinician will walk you through the specifics, but general prep involves:",
            "items": [
                {
                    "title": "Consultation",
                    "desc": "A detailed consultation to evaluate the health of your scalp and suitability for the treatment.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "Avoid Blood Thinners",
                    "desc": "Discontinue any blood thinners (if safe) a few days before the procedure.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Stay Hydrated",
                    "desc": "Hydrate to keep your blood in tip-top shape for extraction.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Wash Your Hair",
                    "desc": "Wash your scalp well on the day of your appointment.",
                    "icon": "fa-solid fa-shower"
                },
                {
                    "title": "Avoid Smoking and Alcohol",
                    "desc": "Avoid smoking or drinking alcohol for at least 48 hours before the treatment.",
                    "icon": "fa-solid fa-smoking-ban"
                }
            ]
        },
        "results": {
            "heading": "Results of PRP Biotin Hair Loss Treatment",
            "subheading": "",
            "para": "Results of PRP Biotin Hair Loss Treatment are exceptional, and patients experience thicker, fuller, and healthier hair. Here's what to expect post-treatment:",
            "items": [
                {
                    "title": "Initial Improvement",
                    "desc": "Most patients notice reduced hair shedding and improved texture after a few initial sessions.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "New Hair Growth",
                    "desc": "Within 3 to 4 months of such initial sessions, new hair starts growing in previously bald spots which increases hair density and volume.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "Full Results",
                    "desc": "Usually, the optimal results can be observed in a few months (about 6 to 12) as hair follows its growth cycle and renews itself.",
                    "icon": "fa-solid fa-award"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "Is PRP treatment painful?",
                    "answer": "PRP treatment involves minimal discomfort. Topical numbing cream is often applied to reduce any sensation during the procedure."
                },
                {
                    "question": "How long does it take to see results from PRP facial treatment?",
                    "answer": "Results typically become visible after 2-3 weeks, with continued improvement over several months as collagen production increases."
                },
                {
                    "question": "How many sessions are needed?",
                    "answer": "For optimal results, 3-4 sessions spaced a month apart are recommended. Maintenance treatments can be done once or twice a year."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "PRP treatment is generally safe, with minimal side effects such as mild swelling, redness, or bruising, which subside within a few days."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg",
            "beforeAfter": [
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/hairprp-1.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/hairprp-2.jpg",
                "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/hairprp-3.jpg"
            ]
        },
        "product": {
            "id": 115,
            "title": "Biotin PRP Hair Loss Treatment",
            "price": 450,
            "description": "Biotin PRP Hair Loss Treatment in Dubai is a revolutionary procedure combining platelet-rich plasma and biotin to naturally stimulate hair growth, restore volume, and improve scalp health. Ideal for men and women experiencing hair thinning or early hair loss."
        }
    },

    "ar": {
        "url": "",
        "meta": {
            "title": "علاج تساقط الشعر بالبيوتين والبلازما في دبي | إيليت بودي هوم",
            "description": "جرب علاج تساقط الشعر المتقدم بالبيوتين والبلازما في دبي عند إيليت بودي هوم. استعد ثقتك بنفسك بهذا الحل القوي غير الجراحي للشعر الخفيف.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/prp-biotin-hair-loss-treatment/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "علاج تساقط الشعر بالبيوتين والبلازما في دبي | إيليت بودي هوم",
                "description": "جرب علاج تساقط الشعر المتقدم بالبيوتين والبلازما في دبي عند إيليت بودي هوم. استعد ثقتك بنفسك بهذا الحل القوي غير الجراحي للشعر الخفيف.",
                "url": "https://elitebodyhome.com/services/prp-biotin-hair-loss-treatment/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2024-12-30T09:27:20+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج تساقط الشعر بالبيوتين والبلازما",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "علاج تساقط الشعر بالبيوتين والبلازما في دبي | إيليت بودي هوم",
                "description": "اكتشف علاج تساقط الشعر الآمن والفعال بالبيوتين والبلازما في دبي عند إيليت بودي هوم. عزز نمو شعرك بالعلاج غير الجراحي الخبير.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-36.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "علاج تساقط الشعر بالبيوتين والبلازما"],
        "hero": {
            "title": "علاج الوجه بالبلازما في دبي",
            "subtitle": "أطلق العنان للتألق الطبيعي لبشرتك مع البلازما",
            "description": "يمكن أن يكون اختيار نظام علاجي لعلاج تساقط الشعر خيارًا مفيدًا يعيد الثقة والصورة الذاتية."
        },
        "howItWorks": {
            "title": "ما هو علاج تساقط الشعر بالبيوتين والبلازما؟",
            "description": "يجمع هذا العلاج بين قوة البلازما الغنية بالصفائح الدموية (PRP) والبيوتين، وهو فيتامين أساسي لصحة الشعر. سواء كنت تعاني من الشعر الخفيف، أو الصلع الذكوري أو الأنثوي، أو تساقط الشعر، يمكن أن يكون هذا العلاج طريقة طبيعية وبسيطة لإحياء نمو الشعر."
        },
        "procedureSteps": {
            "heading": "خطوات الإجراء",
            "para": "علاج تساقط الشعر بالبيوتين والبلازما (PRP) هو مزيج من طريقتين فعالتين للغاية؛ استخلاص البلازما الغنية بالصفائح الدموية من دمك الخاص وحقن البيوتين، مما يخلق خليطًا قويًا قادرًا على تغذية فروة رأسك. بهذه التقنية يتم تجديد بصيلات الشعر، والحفاظ على الدورة الدموية وزيادة الكثافة.",
            "steps": [
                {
                    "title": "الاستشارة",
                    "desc": "يبدأ علاج البيوتين والبلازما باستشارة مع أخصائي لتقييم مشاكل تساقط الشعر لديك وتحديد ما إذا كان هذا العلاج مناسبًا لك.",
                    "icon": "fa-solid fa-comment-medical"
                },
                {
                    "title": "سحب الدم",
                    "desc": "يتم سحب بضعة مليلترات من الدم ووضعها في أنبوب PRP مع البيوتين لتأثيرات تجديدية.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "تحضير البلازما",
                    "desc": "يتم وضع الدم في جهاز طرد مركزي لفصل البلازما الغنية بالصفائح الدموية (PRP) عن المكونات الأخرى، مما يؤدي إلى تركيز عالٍ.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "حقن فروة الرأس",
                    "desc": "يتم حقن البلازما مع البيوتين في مناطق فروة الرأس التي تعاني من الشعر الخفيف أو التساقط.",
                    "icon": "fa-solid fa-needle"
                },
                {
                    "title": "تغذية بالبيوتين",
                    "desc": "يتم تعزيز البلازما بالبيوتين، وهو فيتامين يساعد شعرك على النمو بشكل صحي.",
                    "icon": "fa-solid fa-pills"
                },
                {
                    "title": "الشفاء والنتائج",
                    "desc": "تعمل البلازما على بصيلات الشعر بمرور الوقت لتعزيز نمو الشعر الطبيعي وزيادة السماكة. للحصول على أفضل النتائج، قد يوصى بجلسات متعددة.",
                    "icon": "fa-solid fa-spa"
                }
            ]
        },
        "benefits": {
            "heading": "إذن، ما هي فوائد البلازما والبيوتين لتساقط الشعر؟",
            "subheading": "البلازما + البيوتين: دليل عن كيفية مساعدة البلازما والبيوتين في مكافحة تساقط الشعر؟",
            "para": "يعد مزيج البلازما والبيوتين قوة متكاملة لاستعادة سماكة شعرك ولمعانه. فيما يلي الفوائد الرئيسية:",
            "items": [
                {
                    "title": "تحفيز نمو الشعر",
                    "desc": "تحتوي البلازما الغنية بالصفائح الدموية (PRP) على عوامل نمو تحفز بصيلات الشعر الخاملة وتعزز استئناف نمو الشعر.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "تحسين قوة الشعر",
                    "desc": "يساعد البيوتين في نمو الشعر بشكل أقوى وأكثر سماكة دون خطر التقصف أو الخفة.",
                    "icon": "fa-solid fa-shield-halved"
                },
                {
                    "title": "طبيعي وغير جراحي",
                    "desc": "بما أن البلازما تأتي من دمك الخاص، فإن العلاج آمن ولديه مخاطر قليلة لحدوث رد فعل تحسسي.",
                    "icon": "fa-solid fa-leaf"
                },
                {
                    "title": "علاج قابل للتخصيص",
                    "desc": "يمكن تخصيص العلاج ليناسب الاحتياجات الفردية اعتمادًا على درجة تساقط الشعر وحالة فروة الرأس.",
                    "icon": "fa-solid fa-sliders"
                },
                {
                    "title": "وقت تعافي قصير",
                    "desc": "لا يلزم وقت للتعافي؛ يمكن للمرضى العودة إلى أنشطتهم الطبيعية على الفور.",
                    "icon": "fa-solid fa-clock"
                },
                {
                    "title": "تعزيز صحة فروة الرأس",
                    "desc": "من المعروف أن البيوتين يعزز صحة فروة الرأس ويشجع على نمو شعر أكثر صحة بشكل عام.",
                    "icon": "fa-solid fa-heart-pulse"
                }
            ]
        },
        "eligibility": {
            "heading": "من يمكنه الخضوع لعلاج تساقط الشعر بالبلازما والبيوتين؟",
            "subheading": "",
            "para": "علاج تساقط الشعر بالبيوتين والبلازما مناسب للعديد من الأشخاص الذين يعانون من تساقط الشعر. يستهدف هذا العلاج أي شخص يعاني من الشعر الخفيف، أو تساقط الشعر المبكر، أو حتى الصلع المتقدم. إليك من يمكنه الاستفادة أكثر:",
            "items": [
                {
                    "title": "الرجال والنساء الذين يعانون من الصلع الوراثي",
                    "desc": "(الصلع الذكوري أو الأنثوي)",
                    "icon": "fa-solid fa-user"
                },
                {
                    "title": "الأشخاص الذين يعانون من الشعر الخفيف",
                    "desc": "بسبب التقدم في العمر أو التغيرات الهرمونية.",
                    "icon": "fa-solid fa-user-clock"
                },
                {
                    "title": "النساء بعد الولادة",
                    "desc": "النساء اللواتي وضعن حديثًا ويعانين من تساقط الشعر بعد الحمل",
                    "icon": "fa-solid fa-person-breastfeeding"
                },
                {
                    "title": "تاريخ عائلي من الشعر الخفيف",
                    "desc": "",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "تساقط الشعر المؤقت",
                    "desc": "الأشخاص الذين يعانون من تساقط الشعر بسبب المرض أو الجراحة التي تسبب تساقط الشعر المؤقت.",
                    "icon": "fa-solid fa-user-injured"
                },
                {
                    "title": "مرضى الثعلبة",
                    "desc": "المرضى الذين يعانون من الثعلبة البقعية أو مشاكل أخرى في فروة الرأس (على الرغم من الحاجة إلى استشارة)",
                    "icon": "fa-solid fa-user-md"
                }
            ]
        },
        "preparationTips": {
            "heading": "كيف تستعد لعلاج تساقط الشعر بالبلازما والبيوتين؟",
            "subheading": "",
            "para": "سيشرح لك الطبيب التفاصيل، ولكن التحضير العام يشمل:",
            "items": [
                {
                    "title": "الاستشارة",
                    "desc": "استشارة مفصلة لتقييم صحة فروة رأسك ومدى ملاءمة العلاج لك.",
                    "icon": "fa-solid fa-comments"
                },
                {
                    "title": "تجنب مميعات الدم",
                    "desc": "توقف عن تناول أي مميعات للدم (إذا كان ذلك آمنًا) قبل بضعة أيام من الإجراء.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "حافظ على ترطيب جسمك",
                    "desc": "اشرب الماء للحفاظ على دمك في أفضل حالة للسحب.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "اغسل شعرك",
                    "desc": "اغسل فروة رأسك جيدًا في يوم موعدك.",
                    "icon": "fa-solid fa-shower"
                },
                {
                    "title": "تجنب التدخين والكحول",
                    "desc": "تجنب التدخين أو شرب الكحول لمدة 48 ساعة على الأقل قبل العلاج.",
                    "icon": "fa-solid fa-smoking-ban"
                }
            ]
        },
        "results": {
            "heading": "نتائج علاج تساقط الشعر بالبلازما والبيوتين",
            "subheading": "",
            "para": "نتائج علاج تساقط الشعر بالبلازما والبيوتين استثنائية، حيث يلاحظ المرضى شعرًا أكثر سماكة وامتلاءً وصحة. إليك ما يمكن توقعه بعد العلاج:",
            "items": [
                {
                    "title": "تحسن أولي",
                    "desc": "يلاحظ معظم المرضى انخفاضًا في تساقط الشعر وتحسنًا في الملمس بعد بضع جلسات أولية.",
                    "icon": "fa-solid fa-arrow-up"
                },
                {
                    "title": "نمو شعر جديد",
                    "desc": "في غضون 3 إلى 4 أشهر من هذه الجلسات الأولية، يبدأ الشعر الجديد في النمو في المناطق الصلعاء سابقًا مما يزيد من كثافة الشعر وحجمه.",
                    "icon": "fa-solid fa-seedling"
                },
                {
                    "title": "النتائج الكاملة",
                    "desc": "عادةً ما يمكن ملاحظة النتائج المثلى في بضعة أشهر (حوالي 6 إلى 12) حيث يتبع الشعر دورة نموه ويتجدد.",
                    "icon": "fa-solid fa-award"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "هل علاج البلازما مؤلم؟",
                    "answer": "يتضمن علاج البلازما حدًا أدنى من الانزعاج. غالبًا ما يتم تطبيق كريم مخدر لتقليل أي إحساس أثناء الإجراء."
                },
                {
                    "question": "كم من الوقت يستغرق ظهور نتائج علاج الوجه بالبلازما؟",
                    "answer": "عادةً ما تصبح النتائج مرئية بعد 2-3 أسابيع، مع استمرار التحسن على مدار عدة أشهر مع زيادة إنتاج الكولاجين."
                },
                {
                    "question": "كم عدد الجلسات المطلوبة؟",
                    "answer": "للحصول على أفضل النتائج، يوصى بـ 3-4 جلسات بفاصل شهر بينها. يمكن إجراء جلسات الصيانة مرة أو مرتين في السنة."
                },
                {
                    "question": "هل هناك أي آثار جانبية؟",
                    "answer": "علاج البلازما آمن بشكل عام، مع آثار جانبية قليلة مثل التورم الخفيف أو الاحمرار أو الكدمات، والتي تختفي في غضون أيام قليلة."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []

    }
};
